import { css } from '@microsoft/fast-element';
import { neutralStealthFillActive, neutralStealthFillHover, neutralStealthFillRest, getToken } from '../../design-tokens';
import { heightNumber } from '../size';
import { display } from '../../foundation/display';
import { variantBehavior } from '../behaviors';

export interface IButtonStyles {
    variant: ButtonVariant;
    color: string;
}

export type ButtonVariant =
    'filled'
    | 'outline'
    | 'stealth'
    | 'link'
    | 'link-light'

export const buttonStyles = (interactivitySelector: string, nonInteractivitySelector: string) => css<IButtonStyles>`
    :host([disabled]) {
        opacity: ${getToken('color-disabled-opacity')};
        cursor: not-allowed;
    }
    ${BaseButtonStyles(
    interactivitySelector,
    nonInteractivitySelector
)}
`.withBehaviors(
    variantBehavior(
        'filled',
        FilledStyles(
            interactivitySelector,
            nonInteractivitySelector
        )
    ),
    variantBehavior(
        'outline',
        OutlineButtonStyles(
            interactivitySelector,
            nonInteractivitySelector
        )
    ),
    variantBehavior(
        'stealth',
        StealthButtonStyles(
            interactivitySelector,
            nonInteractivitySelector
        )
    ),
    variantBehavior(
        'link',
        LinkStyles(
            interactivitySelector,
            nonInteractivitySelector
        )
    ),
    variantBehavior(
        'link-light',
        LinkLightStyles(
            interactivitySelector,
            nonInteractivitySelector
        )
    )
);

export const BaseButtonStyles = (interactivitySelector: string, nonInteractivitySelector: string) => css`
    ${display('inline-flex')} :host {
        justify-content: center;
        align-items: center;

        border-style: solid;
        border-color: transparent !important;
        /* TODO: Remove important after tailwind is removed: https://github.com/tailwindlabs/tailwindcss/discussions/2261 */
        border-width: calc(${getToken('space-stroke-width')} * 1px) !important;
        border-radius: calc(${getToken('space-button-corner-radius')} * 1px);
        height: calc(${heightNumber} * 1px);
        min-width: calc(${heightNumber} * 0.5px);

        font-weight: ${getToken('ui-button-base-typography-font-weight')};

        fill: currentColor;
        background-color: var(--background-color);

        cursor: pointer;

        -webkit-appearance: none !important;
    }

    :host .control {
        display: inline-flex;

        flex-grow: 1;
        justify-content: inherit;
        align-items: inherit;

        box-sizing: border-box;

        border: none;
        border-radius: inherit;

        padding: 0 calc((10 + (${getToken('space-design-unit')} * 2 * ${getToken('space-density')})) * 1px);
        height: inherit;

        background: transparent;
        color: inherit;
        fill: inherit;

        font-family: inherit;
        font-size: inherit;
        font-weight: inherit;
        white-space: nowrap;

        outline: none;
        text-decoration: none;
        cursor: inherit;
    }

    .control,
    .end,
    .start {
        font: inherit;
    }

    .control.icon-only {
        padding: 0;
        line-height: 0;
    }

    .control:focus-visible {
        box-shadow: 0 0 0 calc(${getToken('space-focus-stroke-width')} * 1px) var(--background-color) inset,
            0 0 0 calc(${getToken('space-focus-stroke-width')} * 2px) currentColor inset;
    }

    .content {
        pointer-events: none;
    }

    .start,
    .end {
        display: flex;
        pointer-events: none;
    }

    .start {
        margin-inline-end: calc(${getToken('space-design-unit')} * 2px);
    }

    .end {
        margin-inline-start: calc(${getToken('space-design-unit')} * 2px);
    }
`;

// TODO: This is just terrible, really need to figure out a better way to do this.
export const FilledStyles = (interactivitySelector: string, nonInteractivitySelector: string) => css`
    :host([color=neutral]) {
        --background-color: ${getToken('ui-button-filled-color-neutral-background-rest')};
        border-color: ${getToken('ui-button-filled-color-neutral-stroke-rest')} !important;
        color: ${getToken('ui-button-filled-color-neutral-text-rest')};
    }

    :host([color=neutral]${interactivitySelector}:hover) {
        --background-color: ${getToken('ui-button-filled-color-neutral-background-hover')};
        border-color: ${getToken('ui-button-filled-color-neutral-stroke-hover')} !important;
        color: ${getToken('ui-button-filled-color-neutral-text-hover')};
    }

    :host([color=neutral]${interactivitySelector}:active) {
        --background-color: ${getToken('ui-button-filled-color-neutral-background-active')};
        border-color: ${getToken('ui-button-filled-color-neutral-stroke-active')} !important;
        color: ${getToken('ui-button-filled-color-neutral-text-active')};
    }

    :host([color=neutral]):has(.control:focus-visible) {
        --background-color: ${getToken('ui-button-filled-color-neutral-background-focus')};
        border-color: ${getToken('ui-button-filled-color-neutral-stroke-focus')} !important;
        color: ${getToken('ui-button-filled-color-neutral-text-focus')};
    }

    :host([color=primary]) {
        --background-color: ${getToken('ui-button-filled-color-primary-background-rest')};
        border-color: ${getToken('ui-button-filled-color-primary-stroke-rest')} !important;
        color: ${getToken('ui-button-filled-color-primary-text-rest')};
    }

    :host([color=primary]${interactivitySelector}:hover) {
        --background-color: ${getToken('ui-button-filled-color-primary-background-hover')};
        border-color: ${getToken('ui-button-filled-color-primary-stroke-hover')} !important;
        color: ${getToken('ui-button-filled-color-primary-text-hover')};
    }

    :host([color=primary]${interactivitySelector}:active) {
        --background-color: ${getToken('ui-button-filled-color-primary-background-active')};
        border-color: ${getToken('ui-button-filled-color-primary-stroke-active')} !important;
        color: ${getToken('ui-button-filled-color-primary-text-active')};
    }

    :host([color=primary]):has(.control:focus-visible) {
        --background-color: ${getToken('ui-button-filled-color-primary-background-focus')};
        border-color: ${getToken('ui-button-filled-color-primary-stroke-focus')} !important;
        color: ${getToken('ui-button-filled-color-primary-text-focus')};
    }

    :host([color=secondary]) {
        --background-color: ${getToken('ui-button-filled-color-secondary-background-rest')};
        border-color: ${getToken('ui-button-filled-color-secondary-stroke-rest')} !important;
        color: ${getToken('ui-button-filled-color-secondary-text-rest')};
    }

    :host([color=secondary]${interactivitySelector}:hover) {
        --background-color: ${getToken('ui-button-filled-color-secondary-background-hover')};
        border-color: ${getToken('ui-button-filled-color-secondary-stroke-hover')} !important;
        color: ${getToken('ui-button-filled-color-secondary-text-hover')};
    }

    :host([color=secondary]${interactivitySelector}:active) {
        --background-color: ${getToken('ui-button-filled-color-secondary-background-active')};
        border-color: ${getToken('ui-button-filled-color-secondary-stroke-active')} !important;
        color: ${getToken('ui-button-filled-color-secondary-text-active')};
    }

    :host([color=secondary]):has(.control:focus-visible) {
        --background-color: ${getToken('ui-button-filled-color-secondary-background-focus')};
        border-color: ${getToken('ui-button-filled-color-secondary-stroke-focus')} !important;
        color: ${getToken('ui-button-filled-color-secondary-text-focus')};
    }
`;

export const LinkStyles = (interactivitySelector: string, nonInteractivitySelector: string) => css`
        :host {
            height: auto;
            font-family: inherit;
            font-size: inherit;
            line-height: inherit;
            min-width: auto;
            background: transparent;
            /* needs important because of tailwind */
            border: none !important;
            box-shadow: none;
        }

        :host .control {
            display: inline;
            padding: 0;
            text-decoration: underline;
            text-decoration-thickness: 1px;
        }

        :host(${interactivitySelector}),
        :host(${interactivitySelector}:hover),
        :host(${interactivitySelector}:active) {
            background: transparent;
        }

        .control:focus-visible {
            box-shadow: none;
        }

        :host .control${interactivitySelector}:active {
            text-decoration: none;
        }

        .control:focus-visible {
            text-decoration: underline overline;
            text-decoration-thickness: calc(${getToken('space-focus-stroke-width')} * 1px);
        }

        :host([color=neutral]) {
            color: ${getToken('ui-button-link-color-neutral-text-rest')};
        }

        :host([color=neutral]${interactivitySelector}:hover) {
            color: ${getToken('ui-button-link-color-neutral-text-hover')};
        }

        :host([color=neutral]${interactivitySelector}:active) {
            color: ${getToken('ui-button-link-color-neutral-text-active')};
        }

        :host([color=neutral]):has(.control:focus-visible) {
            color: ${getToken('ui-button-link-color-neutral-text-focus')};
        }

        :host([color=primary]) {
            color: ${getToken('ui-button-link-color-primary-text-rest')};
        }

        :host([color=primary]${interactivitySelector}:hover) {
            color: ${getToken('ui-button-link-color-primary-text-hover')};
        }

        :host([color=primary]${interactivitySelector}:active) {
            color: ${getToken('ui-button-link-color-primary-text-active')};
        }

        :host([color=primary]):has(.control:focus-visible) {
            color: ${getToken('ui-button-link-color-primary-text-focus')};
        }

        :host([color=secondary]) {
            color: ${getToken('ui-button-link-color-secondary-text-rest')};
        }

        :host([color=secondary]${interactivitySelector}:hover) {
            color: ${getToken('ui-button-link-color-secondary-text-hover')};
        }

        :host([color=secondary]${interactivitySelector}:active) {
            color: ${getToken('ui-button-link-color-secondary-text-active')};
        }

        :host([color=secondary]):has(.control:focus-visible) {
            color: ${getToken('ui-button-link-color-secondary-text-focus')};
        }

        :host([color=system]) {
            color: LinkText;
        }

        :host([color=system]${interactivitySelector}:hover) {
            color: SelectedItem;
        }

        :host([color=system]${interactivitySelector}:active) {
            color: ActiveText;
        }

        :host([color=system]):has(.control:focus-visible) {
            color: SelectedItem;
        }
    `;

export const LinkLightStyles = (interactivitySelector: string, nonInteractivitySelector: string) => css`
        :host {
            height: auto;
            font-family: inherit;
            font-size: inherit;
            line-height: inherit;
            min-width: auto;
            background: transparent;
            /* needs important because of tailwind */
            border: none !important;
            box-shadow: none;
        }

        :host .control {
            display: inline;
            padding: 0;
            text-decoration-thickness: 1px;
        }
        :host(${interactivitySelector}),
        :host(${interactivitySelector}:hover),
        :host(${interactivitySelector}:active) {
            background: transparent;
        }
        .control:focus-visible {
            box-shadow: none;
        }

        .control.icon-only:focus-visible ::slotted(natgen-icon) {
            outline: calc(${getToken('space-focus-stroke-width')} * 1px) solid currentColor;
        }

        :host .control${interactivitySelector}:hover,
        :host .control${interactivitySelector}:active {
            text-decoration: underline;
        }
        .control:focus-visible {
            text-decoration: underline overline;
            text-decoration-thickness: calc(${getToken('space-focus-stroke-width')} * 1px);
        }

        :host([color=neutral]) {
            color: ${getToken('ui-button-link-color-neutral-text-rest')};
        }

        :host([color=neutral]${interactivitySelector}:hover) {
            color: ${getToken('ui-button-link-color-neutral-text-hover')};
        }

        :host([color=neutral]${interactivitySelector}:active) {
            color: ${getToken('ui-button-link-color-neutral-text-active')};
        }

        :host([color=neutral]):has(.control:focus-visible) {
            color: ${getToken('ui-button-link-color-neutral-text-focus')};
        }

        :host([color=primary]) {
            color: ${getToken('ui-button-link-color-primary-text-rest')};
        }

        :host([color=primary]${interactivitySelector}:hover) {
            color: ${getToken('ui-button-link-color-primary-text-hover')};
        }

        :host([color=primary]${interactivitySelector}:active) {
            color: ${getToken('ui-button-link-color-primary-text-active')};
        }

        :host([color=primary]):has(.control:focus-visible) {
            color: ${getToken('ui-button-link-color-primary-text-focus')};
        }

        :host([color=secondary]) {
            color: ${getToken('ui-button-link-color-secondary-text-rest')};
        }

        :host([color=secondary]${interactivitySelector}:hover) {
            color: ${getToken('ui-button-link-color-secondary-text-hover')};
        }

        :host([color=secondary]${interactivitySelector}:active) {
            color: ${getToken('ui-button-link-color-secondary-text-active')};
        }

        :host([color=secondary]):has(.control:focus-visible) {
            color: ${getToken('ui-button-link-color-secondary-text-focus')};
        }
    `;

export const OutlineButtonStyles = (interactivitySelector: string, nonInteractivitySelector: string) => css`
        :host {
            border-width: calc(${getToken('space-stroke-width')} * 1px) !important;
        }

        .control:focus-visible {
            box-shadow: none;
            outline: calc(${getToken('space-focus-stroke-width')} * 1px) solid currentColor;
            outline-offset: calc(${getToken('space-design-unit')} *  1px);
        }

        :host([color=neutral]) {
            --background-color: ${getToken('ui-button-outline-color-neutral-background-rest')};
            border-color: ${getToken('ui-button-outline-color-neutral-stroke-rest')} !important;
            color: ${getToken('ui-button-outline-color-neutral-text-rest')};
        }

        :host([color=neutral]${interactivitySelector}:hover) {
            --background-color: ${getToken('ui-button-outline-color-neutral-background-hover')};
            border-color: ${getToken('ui-button-outline-color-neutral-stroke-hover')} !important;
            color: ${getToken('ui-button-outline-color-neutral-text-hover')};
        }

        :host([color=neutral]${interactivitySelector}:active) {
            --background-color: ${getToken('ui-button-outline-color-neutral-background-active')};
            border-color: ${getToken('ui-button-outline-color-neutral-stroke-active')} !important;
            color: ${getToken('ui-button-outline-color-neutral-text-active')};
        }

        :host([color=neutral]):has(.control:focus-visible) {
            --background-color: ${getToken('ui-button-outline-color-neutral-background-focus')};
            border-color: ${getToken('ui-button-outline-color-neutral-stroke-focus')} !important;
            color: ${getToken('ui-button-outline-color-neutral-text-focus')};
        }

        :host([color=primary]) {
            --background-color: ${getToken('ui-button-outline-color-primary-background-rest')};
            border-color: ${getToken('ui-button-outline-color-primary-stroke-rest')} !important;
            color: ${getToken('ui-button-outline-color-primary-text-rest')};
        }

        :host([color=primary]${interactivitySelector}:hover) {
            --background-color: ${getToken('ui-button-outline-color-primary-background-hover')};
            border-color: ${getToken('ui-button-outline-color-primary-stroke-hover')} !important;
            color: ${getToken('ui-button-outline-color-primary-text-hover')};
        }

        :host([color=primary]${interactivitySelector}:active) {
            --background-color: ${getToken('ui-button-outline-color-primary-background-active')};
            border-color: ${getToken('ui-button-outline-color-primary-stroke-active')} !important;
            color: ${getToken('ui-button-outline-color-primary-text-active')};
        }

        :host([color=primary]):has(.control:focus-visible) {
            --background-color: ${getToken('ui-button-outline-color-primary-background-focus')};
            border-color: ${getToken('ui-button-outline-color-primary-stroke-focus')} !important;
            color: ${getToken('ui-button-outline-color-primary-text-focus')};
        }

        :host([color=secondary]) {
            --background-color: ${getToken('ui-button-outline-color-secondary-background-rest')};
            border-color: ${getToken('ui-button-outline-color-secondary-stroke-rest')} !important;
            color: ${getToken('ui-button-outline-color-secondary-text-rest')};
        }

        :host([color=secondary]${interactivitySelector}:hover) {
            --background-color: ${getToken('ui-button-outline-color-secondary-background-hover')};
            border-color: ${getToken('ui-button-outline-color-secondary-stroke-hover')} !important;
            color: ${getToken('ui-button-outline-color-secondary-text-hover')};
        }

        :host([color=secondary]${interactivitySelector}:active) {
            --background-color: ${getToken('ui-button-outline-color-secondary-background-active')};
            border-color: ${getToken('ui-button-outline-color-secondary-stroke-active')} !important;
            color: ${getToken('ui-button-outline-color-secondary-text-active')};
        }

        :host([color=secondary]):has(.control:focus-visible) {
            --background-color: ${getToken('ui-button-outline-color-secondary-background-focus')};
            border-color: ${getToken('ui-button-outline-color-secondary-stroke-focus')} !important;
            color: ${getToken('ui-button-outline-color-secondary-text-focus')};
        }
    `;

export const StealthButtonStyles = (interactivitySelector: string, nonInteractivitySelector: string) => css`
        :host {
            background: ${neutralStealthFillRest};
        }

        :host(${interactivitySelector}:hover) {
            background: ${neutralStealthFillHover};
        }

        :host(${interactivitySelector}:active) {
            background: ${neutralStealthFillActive};
        }

        .control:focus-visible {
            outline: calc(${getToken('space-focus-stroke-width')} * 1px) solid currentColor;
        }
    `;
